// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useRef, useEffect, useCallback } from 'react';
import { streamAskHelios } from '../services/askHeliosService';
import { useShellStore } from '../stores/shellStore';
import type { AskHeliosMessage } from '../types';

function createWelcomeMessage(): AskHeliosMessage {
  return {
    role: 'assistant',
    content:
      "I'm Ask Helios. I answer questions about approved SOPs and operating " +
      'procedures, with citations. I’m advisory only — I don’t take actions.',
    timestamp: new Date(),
  };
}

/**
 * Starter prompts for the empty state. Each one lands inside the advisory
 * boundary, so a first-time operator learns what Ask Helios answers by using
 * it rather than by hitting a refusal.
 */
export const SUGGESTED_QUESTIONS = [
  'How do I transfer the UPS off maintenance bypass?',
  'What lockout/tagout steps apply to a CRAH unit?',
  'What can you help me with?',
];

export interface UseAskHeliosReturn {
  messages: AskHeliosMessage[];
  inputValue: string;
  setInputValue: (_value: string) => void;
  isThinking: boolean;
  /** Sanitized progress label while a tool runs (e.g. "Searching the SOP library…"); null otherwise. */
  progressLabel: string | null;
  sessionId: string | undefined;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  handleSendMessage: (_e: React.FormEvent | React.KeyboardEvent) => void;
  handleNewChat: () => void;
  /** True while only the welcome turn is on screen — drives the starter prompts. */
  isEmpty: boolean;
  /** Sends `question` directly, bypassing the input box (starter prompt / retry). */
  askQuestion: (_question: string) => void;
  /** The last question that failed, so the error turn can offer a retry. */
  retryableQuestion: string | null;
}

/**
 * State for the structured Ask Helios chat. Each user turn streams one
 * POST /ai/ask-helios/stream call (SSE) and appends an assistant message
 * carrying the advisory metadata (confidence, citations, disclaimer) once the
 * final envelope arrives. Thread continuity is kept via the returned
 * session_id.
 */
export function useAskHelios(): UseAskHeliosReturn {
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const seed = useShellStore(s => s.askHeliosSeed);
  const [messages, setMessages] = useState<AskHeliosMessage[]>([createWelcomeMessage()]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [retryableQuestion, setRetryableQuestion] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  // Guards against a slow response landing after the user started a new chat.
  const requestSeq = useRef(0);

  const clearAskHeliosSeed = useShellStore(s => s.clearAskHeliosSeed);

  const handleNewChat = useCallback(() => {
    requestSeq.current += 1;
    setIsThinking(false);
    setProgressLabel(null);
    setSessionId(undefined);
    setRetryableQuestion(null);
    setMessages([createWelcomeMessage()]);
    // The panel header reads the seed too — leaving it set would keep showing
    // an alarm context the fresh thread no longer carries.
    clearAskHeliosSeed();
  }, [clearAskHeliosSeed]);

  const askQuestion = useCallback(
    (raw: string) => {
      const question = raw.trim();
      if (!question || isThinking) return;

      const seq = ++requestSeq.current;
      setRetryableQuestion(null);
      setMessages(prev => [
        ...prev,
        { role: 'user', content: question, timestamp: new Date() },
      ]);
      setIsThinking(true);
      setProgressLabel(null);

      streamAskHelios(
        {
          question,
          session_id: sessionId,
          current_page: window.location.href,
          dc_id: selectedSiteId ?? undefined,
          // Seed the alarm only on the first turn — after that it lives in the
          // Foundry thread history, so re-sending would be redundant.
          context_seed: sessionId ? undefined : (seed ?? undefined),
        },
        label => {
          if (seq !== requestSeq.current) return;
          setProgressLabel(label);
        },
        res => {
          if (seq !== requestSeq.current) return; // superseded by new chat
          if (res.session_id) setSessionId(res.session_id);
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: res.text,
              timestamp: new Date(),
              confidence: res.confidence,
              citations: res.citations,
              contextChips: res.context_chips,
              route: res.route,
              abstained: res.abstained,
              disclaimer: res.disclaimer,
            },
          ]);
          setIsThinking(false);
          setProgressLabel(null);
        },
        () => {
          if (seq !== requestSeq.current) return;
          setRetryableQuestion(question);
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: 'Sorry — Ask Helios is temporarily unavailable.',
              timestamp: new Date(),
              error: true,
            },
          ]);
          setIsThinking(false);
          setProgressLabel(null);
        }
      ).then(() => {
        // Safety net: a stream that ends without an envelope or error event
        // (malformed/truncated) would otherwise leave isThinking stuck true.
        if (seq === requestSeq.current) setIsThinking(false);
      });
    },
    [isThinking, sessionId, selectedSiteId, seed]
  );

  const handleSendMessage = useCallback(
    (e: React.FormEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (!inputValue.trim() || isThinking) return;
      askQuestion(inputValue);
      setInputValue('');
    },
    [inputValue, isThinking, askQuestion]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  return {
    messages,
    inputValue,
    setInputValue,
    isThinking,
    progressLabel,
    sessionId,
    messagesEndRef,
    handleSendMessage,
    handleNewChat,
    isEmpty: messages.length <= 1,
    askQuestion,
    retryableQuestion,
  };
}
